import { Button, Dialog, DialogActions, DialogContent, DialogTitle, Grid, Link, Typography } from '@material-ui/core'
import { FindInPage } from '@material-ui/icons/'
import ExpandMoreIcon from '@material-ui/icons/ExpandMore'
import { useEffect, useRef, useState } from 'react'
import { onEditAction } from '../../core/audit'
import {
  CollectionTable,
  Column,
  FilterControl,
  auditColumn,
  deleteColumn,
  editColumn,
  lineNumberColumn,
  noteColumn,
  rowColumn,
  shimColumn,
} from '../../core/collections'
import { Auto, CheckBox, DateInline, NumberText, Search, Text } from '../../core/controls'
import { formatLocalDate } from '../../core/date-time'
import { Diagnosis, Procedure } from '../../core/domain'
import { Expansion, ExpansionDetails, ExpansionSummary } from '../../core/expansion'
import { FormApi, formApi } from '../../core/form'
import { personNameWithDegree, truncateSpan } from '../../core/format'
import { IcdCptApi, removeIcdApi } from '../../core/icd-cpt-api'
import { addStickyNote, removeStickyNote } from '../../core/notes-api'
import { orange } from '../../core/theme'
import { UndoRedo } from '../../core/undo-redo'
import {
  Feature,
  WorksheetTypesParam,
  featureEnabled,
  findFeature,
  isAuditReviewWorkflow,
  isReviewWorkflow,
  workflows,
} from '../../core/workflow-api'
import { NO_NOTE_CONTEXT } from '../common/notes'
import { EmCalculator } from '../em-calculator/em-calculator'
import { EmCalculator2 } from '../em-calculator/em-calculator-2'
import { ReasonForVisitApi } from '../reason-for-visit/reason-for-visit-api'

function isPoaFeatureEligible(api: IcdCptApi) {
  const poaFeatureConfig: Feature<WorksheetTypesParam> = findFeature('PresentOnAdmission')
  const poaWorksheetTypes = poaFeatureConfig?.parameters?.worksheetTypes || []
  return poaFeatureConfig?.enabled && poaWorksheetTypes.includes(api.worksheet.worksheetType)
}

type IcdCptFormProps = {
  icdCpt: IcdCptApi
  reasonForVisit?: ReasonForVisitApi
  reasonForVisitEnabled?: boolean
}

export function IcdCptForm({ icdCpt, reasonForVisit, reasonForVisitEnabled }: IcdCptFormProps) {
  const form = useRef(icdCpt.form).current

  const {
    code,
    modifier,
    multiplier,
    links,
    serviceDate,
    provider,
    secondary,
    tertiary,
    textSearch,
    placeOfService,
    presentOnAdmission,
  } = form.controls

  const emCalculatorEnabled = featureEnabled('EMCalculator')

  const reasonForVisitControl = reasonForVisit.form.controls.enabled
  const reasonForVisitSearch = reasonForVisit.form.controls.code
  const swapControl = reasonForVisit.data((s) => s.enabled)

  const { config, secondaryEnabled, tertiaryEnabled } = icdCpt.context
  const hasPlace = config.placeOfService?.length
  const calcConf = icdCpt.calculator((s) => s.config) || icdCpt.calculator.getState().config2
  const poaVisible = isPoaFeatureEligible(icdCpt)
  const emV2 = icdCpt.calculator((s) => s.v2)

  return (
    <>
      {emCalculatorEnabled
        ? calcConf?.supportedNotesAndDrivers &&
          (emV2 ? (
            <EmCalculator2 api={icdCpt.calculator} icdCpt={icdCpt} />
          ) : (
            <EmCalculator api={icdCpt.calculator} icdCpt={icdCpt} />
          ))
        : null}
      <Grid
        container
        item
        spacing={2}
        alignItems="center"
        data-cpt-top-form="true"
        style={{
          width: '100%',
          paddingLeft: 12,
          paddingTop: 6,
          flexWrap: 'nowrap',
        }}
      >
        <Grid item container direction="row" spacing={0} style={{ flexWrap: 'nowrap', width: '17%', paddingRight: 12 }}>
          <Grid
            item
            style={{
              display: swapControl ? undefined : 'none',
              width: '100%',
              paddingTop: reasonForVisitEnabled ? 4 : undefined,
              paddingRight: reasonForVisitEnabled ? 8 : undefined,
            }}
          >
            <Search {...reasonForVisitSearch} />
          </Grid>

          <Grid
            item
            style={{
              display: swapControl ? 'none' : undefined,
              width: '100%',
              paddingTop: reasonForVisitEnabled ? 4 : undefined,
              paddingRight: reasonForVisitEnabled ? 8 : undefined,
            }}
          >
            <Search {...code} />
          </Grid>

          <Grid
            item
            container
            direction="column"
            style={{
              width: 38,
              maxHeight: 36,
              paddingLeft: 4,
              paddingTop: reasonForVisitEnabled ? 0 : 8,
              flexWrap: 'nowrap',
            }}
            spacing={0}
            alignItems="flex-end"
          >
            <Grid item container direction="row" style={{ flexWrap: 'nowrap' }}>
              <Grid
                item
                style={{
                  maxWidth: 24,
                  maxHeight: 24,
                  overflow: 'visible',
                  paddingTop: 2,
                }}
              >
                <CheckBox {...textSearch} />
              </Grid>
              <Grid item style={{ color: 'white', paddingTop: 14 }}>
                Text
              </Grid>
            </Grid>
            {reasonForVisitEnabled && (
              <Grid item container direction="row" style={{ flexWrap: 'nowrap', paddingLeft: 1, marginTop: -10 }}>
                <Grid
                  item
                  style={{
                    maxWidth: 24,
                    maxHeight: 24,
                    overflow: 'visible',
                    paddingTop: 2,
                  }}
                >
                  <CheckBox {...reasonForVisitControl} />
                </Grid>
                <Grid
                  item
                  style={{
                    color: 'white',
                    paddingTop: 14,
                  }}
                >
                  RFV
                </Grid>
              </Grid>
            )}
          </Grid>
        </Grid>

        {poaVisible && (
          <Grid item style={{ paddingLeft: 12, paddingRight: 6, maxWidth: '60px' }}>
            <Search {...presentOnAdmission} />
          </Grid>
        )}

        <Grid style={{ paddingLeft: 12, paddingRight: 12, width: '8%' }}>
          <Text {...modifier} />
        </Grid>
        <Grid style={{ paddingRight: 6, width: '5%' }}>
          <NumberText {...multiplier} />
        </Grid>
        <Grid item style={{ width: '8%' }}>
          <Text {...links} />
        </Grid>
        <Grid item style={{ paddingBottom: 36, width: '12%' }}>
          <DateInline {...serviceDate} />
        </Grid>
        {hasPlace ? (
          <Grid item style={{ flexGrow: 1 }}>
            <Auto {...placeOfService} />
          </Grid>
        ) : null}

        <Grid item style={{ flexGrow: 1 }}>
          <Auto {...provider} />
        </Grid>
        {secondaryEnabled && (
          <Grid item style={{ flexGrow: 1 }}>
            <Auto {...secondary} />
          </Grid>
        )}
        {tertiaryEnabled && (
          <Grid item style={{ flexGrow: 1 }}>
            <Auto {...tertiary} />
          </Grid>
        )}
      </Grid>
    </>
  )
}

function AuditSummary({ left, right }) {
  return (
    <Grid container direction="row" alignItems="center" style={{ width: '100%' }}>
      <Grid item>{left}</Grid>
      <Grid item style={{ flexGrow: 1 }} />
      <Grid item>{right}</Grid>
    </Grid>
  )
}

type AuditLinkProps = {
  itemLabel: string
  itemClick: () => void
  listLabel: string
  listClick: () => void
  color?: string
  hide?: boolean
}
export function AuditLinks(props: AuditLinkProps) {
  const { itemLabel, itemClick, listLabel, listClick, color, hide } = props
  const w = workflows.data((s) => s.workflow)
  const show = !hide && w && w.name === 'auditStd'
  if (!show) {
    return null
  }
  const iconColor: any = color || orange
  const onItemClick = (e: any) => {
    e.preventDefault()
    e.stopPropagation()

    if (itemClick) {
      itemClick()
    }
  }

  const onListClick = (e: any) => {
    e.preventDefault()
    e.stopPropagation()

    if (listClick) {
      listClick()
    }
  }

  const label = w.name === 'auditStd' ? 'Audit' : 'Coding Review'

  return (
    <Grid
      container
      direction="row"
      alignItems="center"
      justify="space-between"
      spacing={2}
      style={{ maxWidth: 400, flexWrap: 'nowrap' }}
    >
      <Grid item style={{ flexGrow: 1 }}></Grid>
      <Grid item container direction="row" alignItems="center" spacing={1} style={{ width: 'fit-content' }}>
        <Grid item>
          <FindInPage htmlColor={iconColor} />
        </Grid>
        <Grid item>
          <Typography variant="overline" style={{ color: iconColor }}>
            {label}
          </Typography>
        </Grid>
      </Grid>
      <Grid item>
        <Link onClick={onItemClick} href="#" underline="always" color="primary">
          {itemLabel}
        </Link>
      </Grid>
      <Grid item>
        <Link onClick={onListClick} href="#" underline="always" color="primary">
          {listLabel}
        </Link>
      </Grid>
    </Grid>
  )
}

const rowStyle: any = {
  display: 'flex',
  flexDirection: 'row',
  gap: 24,
  alignItems: 'center',
  flexWrap: 'nowrap',
}

export function IcdCollection(api: IcdCptApi) {
  const { icds, notesApi, context, moveIcd, inactiveIcds } = api
  const { auditICD } = context.config
  const poaVisible = isPoaFeatureEligible(api)

  const openNotes = notesApi.useNotes((s) => s.openDialog)
  const values = icds.data((s) => s.values)
  const isReview = isAuditReviewWorkflow()
  const disableDelete = !icds.data((s) => s.edited)

  const actions = isReview
    ? [auditColumn(onEditAction('Edit ICD Audit', () => icds.actions.refresh(), false, auditICD))]
    : []

  actions.push(
    noteColumn((e) =>
      openNotes(
        NO_NOTE_CONTEXT,
        e.value.note,
        async (note) => {
          await addStickyNote({ code: e.value.diagnosisCode, note: note.note }, e.value)
          icds.actions.refresh()
        },
        () => {
          removeStickyNote(e.value)
        },
      ),
    ),
    editColumn(
      (e) => api.editIcd(e.value),
      (v) => v?.active,
      '',
      (e) => 'icd-edit-btn-' + e.row,
    ),
    deleteColumn(
      (e) => {
        api.removeIcd(e.value)
      },
      (v) => disableDelete && v?.active,
      '',
      (e) => 'icd-delete-btn-' + e.row,
    ),
    shimColumn(),
  )

  const columns: Column[] = [
    lineNumberColumn(),
    {
      name: 'diagnosisCode',
      label: 'Code',
      id: 'icd-code',
      style: { minWidth: '80px' },
      component: (e) => truncateSpan(e.value.diagnosisCode, '80px'),
    },
    {
      name: 'dxDescription',
      style: { width: '50vw', maxWidth: '60vw' },
      id: 'icd-description',
      label: 'Description',
    },
    poaVisible
      ? {
          name: 'presentOnAdmission',
          style: { width: '50vw', maxWidth: '60vw' },
          label: 'Present on Admission',
          component: (e) => {
            const poa = e.value.presentOnAdmission
            if (poa) {
              const poaDescriptor = api.context.config?.presentOnAdmission?.find((c) => c.code === poa)
              return poa + (poaDescriptor ? ' - ' + poaDescriptor.description : '')
            }
          },
        }
      : null,
    ...actions,
  ].filter(Boolean)

  const [expanded, setExpanded] = useState(values.length > 0)
  const [clicked, setClicked] = useState(false)

  useEffect(() => {
    if (!clicked && !expanded) {
      setExpanded(values.length > 0)
    }
  })

  const onClicked = () => {
    setExpanded(!expanded)
    setClicked(true)
  }

  return (
    <Expansion expanded={expanded}>
      <ExpansionSummary onClick={onClicked} expandIcon={<ExpandMoreIcon id="icd_expend_more" />}>
        <AuditSummary
          left={
            <div style={rowStyle}>
              <Typography variant="subtitle2">{`ICD ${values.length ? '- ' + values.length : ''}`}</Typography>
            </div>
          }
          right={
            <div style={rowStyle}>
              <div onClick={(e) => e.stopPropagation()}>
                <UndoRedo api={api.icdChanges} htmlColor={orange} label="icd_cpt" />
              </div>
              <FilterControl api={icds} />
            </div>
          }
        />
      </ExpansionSummary>
      <ExpansionDetails>
        <Grid container direction="column" style={{ width: '100%', flexWrap: 'nowrap' }}>
          <CollectionTable
            name="icd_cpt_table"
            readonly={api.context.readonly}
            onMove={moveIcd}
            noEdit={true}
            draggable={true}
            columns={columns}
            api={icds}
            containerProps={{ elevation: 0 }}
            label={`ICD ${values.length ? '- ' + values.length : ''}`}
          />
          {isReview ? (
            <CollectionTable
              readonly={api.context.readonly}
              headless={true}
              noEdit={true}
              draggable={false}
              columns={columns}
              api={inactiveIcds}
              containerProps={{ elevation: 0 }}
              name="icd_cpt_table_inactive"
              label={`ICD ${values.length ? '- ' + values.length : ''}`}
            />
          ) : null}
        </Grid>
      </ExpansionDetails>
    </Expansion>
  )
}

export function CptCollection(api: IcdCptApi) {
  const { cpts, notesApi, moveCpt, context, cptChanges, inactiveCpts } = api
  const { auditCPT } = context.config
  const { secondaryEnabled, tertiaryEnabled } = context

  const values = cpts.data((s) => s.values)

  const openNotes = notesApi.useNotes((s) => s.openDialog)

  const isReview = isAuditReviewWorkflow()

  const disableDelete = !cpts.data((s) => s.edited)

  const actions = isReview
    ? [
        auditColumn(
          onEditAction(
            'Edit CPT Audit',
            (item) => {
              cpts.actions.refresh()
              cptChanges.update(item, 'UPDATE')
            },
            true,
            auditCPT,
          ),
        ),
      ]
    : []

  actions.push(
    noteColumn((e) =>
      openNotes(
        NO_NOTE_CONTEXT,
        e.value.note,
        async (note) => {
          await addStickyNote({ code: e.value.cptCode, note: note.note }, e.value)
          cpts.actions.refresh()
        },
        () => {
          removeStickyNote(e.value)
          cpts.actions.refresh()
        },
      ),
    ),
    editColumn<Procedure>(
      (e) => api.editCpt(e.value),
      (v) => v?.active,
      '',
      (e) => 'cpt-edit-btn-' + e.row,
    ),
    deleteColumn<Procedure>(
      (e) => {
        api.removeCpt(e.value)
      },
      (v) => disableDelete && v?.active,
      '',
      (e) => 'cpt-delete-btn-' + e.row,
    ),
    shimColumn(),
  )
  const columns: Column<Procedure>[] = [
    rowColumn(),
    {
      label: 'Code',
      name: 'cptCode',
      style: { whiteSpace: 'nowrap' },
    },
    {
      label: 'Mod',
      name: 'modifier',
      style: { whiteSpace: 'nowrap', width: 30, maxWidth: 30 },
    },
    {
      label: 'Mult',
      name: 'multiplier',
      style: { whiteSpace: 'nowrap', width: 20, maxWidth: 20 },
    },
    {
      label: 'Links',
      name: 'links',
      style: { whiteSpace: 'nowrap' },
    },
    {
      label: 'GD',
      name: 'globalDays',
      style: { whiteSpace: 'nowrap', width: 20, maxWidth: 20 },
    },
    {
      label: 'Date',
      name: 'serviceDate',
      style: { whiteSpace: 'nowrap', width: 80, maxWidth: 80 },
      component: (e) => formatLocalDate(e.value.serviceDate),
    },
    {
      label: 'Description',
      name: 'description',
      component: (e) => truncateSpan(e.value.description, '10vw'),
    },
    {
      label: 'Primary',
      name: 'physician',
      component: (e) => truncateSpan(personNameWithDegree(e.value.provider), '5vw'),
    },
    secondaryEnabled
      ? {
          label: 'Secondary',
          name: 'secondaryProvider',
          component: (e) => truncateSpan(personNameWithDegree(e.value.secondaryProvider), '4vw'),
        }
      : null,
    tertiaryEnabled
      ? {
          label: 'Tertiary',
          name: 'tertiaryProvider',
          component: (e) => truncateSpan(personNameWithDegree(e.value.tertiaryProvider), '4vw'),
        }
      : null,
    ...actions,
  ].filter((a) => a !== null)

  const [expanded, setExpanded] = useState(values.length > 0)
  const [clicked, setClicked] = useState(false)

  useEffect(() => {
    if (!clicked && !expanded) {
      setExpanded(values.length > 0)
    }
  })

  const onClicked = () => {
    setExpanded(!expanded)
    setClicked(true)
  }

  return (
    <Expansion expanded={expanded} id="cpt_collection">
      <ExpansionSummary onClick={onClicked} expandIcon={<ExpandMoreIcon id="cpt_collection_expend_more" />}>
        <AuditSummary
          left={
            <div style={rowStyle}>
              <Typography variant="subtitle2">{`CPT ${values.length ? '- ' + values.length : ''}`}</Typography>
            </div>
          }
          right={
            <div style={rowStyle} onClick={(e) => e.stopPropagation()}>
              <UndoRedo api={api.cptChanges} htmlColor={orange} label="cpt_collection" />
              <FilterControl api={cpts} />
            </div>
          }
        />
      </ExpansionSummary>
      <ExpansionDetails>
        <Grid container direction="column" style={{ width: '100%', flexWrap: 'nowrap' }}>
          <CollectionTable
            name="cpt-collection-table"
            readonly={api.context.readonly}
            onMove={moveCpt}
            columns={columns}
            api={cpts}
            draggable={true}
            containerProps={{ elevation: 0 }}
            label={`CPT ${values.length ? '- ' + values.length : ''}`}
          />
          {isReview ? (
            <CollectionTable
              readonly={api.context.readonly}
              headless={true}
              onMove={moveCpt}
              noEdit={true}
              draggable={false}
              columns={columns}
              api={inactiveCpts}
              containerProps={{ elevation: 0 }}
              name="cpt-collection-inactive"
              label={`CPT ${values.length ? '- ' + values.length : ''}`}
            />
          ) : null}
        </Grid>
      </ExpansionDetails>
    </Expansion>
  )
}

export function RemoveIcdDialogue() {
  const dxToRemove = removeIcdApi((s) => s.dxToRemove)
  const dxList = removeIcdApi((s) => s.dxList) || []
  const form: FormApi = formApi({
    values: {
      replacementDx: null,
      removeCpt: false,
    },
    fields: {
      replacementDx: {
        label: 'Optional Replacement ICD Code',
        list: dxList,
        listLabel: (v: Diagnosis) => v.diagnosisCode,
      },
      removeCpt: {
        label: 'Remove CPT if there are no remaining links',
        onChange: () =>
          removeIcdApi.setState({
            removeCpt: removeCpt.selected.getState().value,
          }),
      },
    },
    onSubmit: (v) => {
      removeIcdApi.setState({ replacementDx: v.replacementDx.diagnosisCode })
      removeIcdApi.getState().remove()
    },
  })

  const { replacementDx, removeCpt } = form.controls

  useEffect(() => {
    replacementDx.value.getState().setValue({ dxList })
    removeCpt.selected.getState().setValue(removeIcdApi.getState().removeCpt)
  })

  const close = () => removeIcdApi.setState({ dxToRemove: null })

  return (
    <Dialog open={dxToRemove !== null} maxWidth={false} onClose={close}>
      <DialogTitle style={{ paddingBottom: 0 }}>Remove Diagnosis</DialogTitle>
      <DialogContent style={{ width: 346 }}>
        {dxToRemove && <div>Code to Remove: {dxToRemove.diagnosisCode}</div>}
        <Auto {...replacementDx} />
        <CheckBox {...removeCpt} />
      </DialogContent>
      <DialogActions>
        <Button onClick={close} color="primary" variant="outlined">
          {' '}
          Cancel{' '}
        </Button>
        <Button onClick={() => form.submit()} color="primary" variant="contained">
          {' '}
          Remove{' '}
        </Button>
      </DialogActions>
    </Dialog>
  )
}
